/* global Alpine */

/**
 * Raccolta dati di fatturazione prima del pagamento.
 * Stato salvato (card): config.invoiceRequested + form da API.
 * Bozza modal: modalWantsInvoice + modalForm (non influenzano la card finché non si salva).
 */
export default function bookingInvoice() {
  document.addEventListener('alpine:init', () => {
    Alpine.store('bookingInvoice', {
      wantsInvoice: false,
      isSaved: false,
      blocksPayment: false,
      paymentBlockMessage: '',
    })

    const emptyForm = () => ({
      subject_type: 'private',
      holder: '',
      firstname: '',
      lastname: '',
      vatnumber: '',
      taxcode: '',
      address: '',
      streetnumber: '',
      zipcode: '',
      city: '',
      province: '',
      country: 'IT',
      sdicode: '',
      pec: '',
    })

    Alpine.data('bookingInvoice', () => ({
      config: {},
      modalOpen: false,
      isSaved: false,
      hasInvoiceData: false,

      status: 'ready',
      saving: false,
      saveError: null,
      saved: false,
      fieldErrors: {},

      form: emptyForm(),
      modalWantsInvoice: false,
      modalForm: emptyForm(),

      init() {
        const raw = this.$el.getAttribute('data-invoice-config')
        if (raw) {
          try {
            this.config = JSON.parse(raw)
          } catch {
            this.config = {}
          }
        }
        this.applyInitialData()
      },

      get isEditable() {
        return !!this.config.editable
      },

      /** Preferenza persistita sul server (solo questa guida la card). */
      get serverWantsInvoice() {
        return !!this.config.invoiceRequested
      },

      get isPrivate() {
        return this.form.subject_type === 'private'
      },

      get isPa() {
        return this.form.subject_type === 'pa'
      },

      get isCompany() {
        return (
          this.form.subject_type === 'company' ||
          this.form.subject_type === 'freelancer'
        )
      },

      get modalIsPrivate() {
        return this.modalForm.subject_type === 'private'
      },

      get modalIsPa() {
        return this.modalForm.subject_type === 'pa'
      },

      get modalIsCompany() {
        return (
          this.modalForm.subject_type === 'company' ||
          this.modalForm.subject_type === 'freelancer'
        )
      },

      get showCard() {
        return this.isEditable || this.serverWantsInvoice
      },

      get showInvoiceSummary() {
        return this.serverWantsInvoice && this.hasInvoiceData
      },

      get needsInvoiceData() {
        return (
          this.isEditable && this.serverWantsInvoice && !this.hasInvoiceData
        )
      },

      get blocksPayment() {
        return this.isEditable && this.serverWantsInvoice && !this.isSaved
      },

      updateSavedState() {
        this.isSaved =
          !this.config.invoiceRequested ||
          (this.hasInvoiceData && !!this.config.invoiceRequested)
        this.saved = this.isSaved
      },

      get addressLine() {
        return [
          this.form.address,
          this.form.streetnumber,
          this.form.zipcode,
          this.form.city,
          this.form.province,
        ]
          .filter(Boolean)
          .join(', ')
      },

      cloneForm(source) {
        return { ...emptyForm(), ...source }
      },

      openEditModal(enableInvoice = false) {
        if (!this.isEditable) {
          return
        }
        this.modalForm = this.cloneForm(this.form)
        this.modalWantsInvoice = enableInvoice
          ? true
          : !!this.config.invoiceRequested
        if (enableInvoice && !this.config.invoiceRequested) {
          const prefill = this.config.prefill || {}
          if (prefill.holder && !this.modalForm.holder) {
            this.modalForm.holder = prefill.holder
          }
        }
        this.saveError = null
        this.fieldErrors = {}
        this.modalOpen = true
      },

      closeEditModal() {
        if (this.saving) {
          return
        }
        this.modalOpen = false
        this.saveError = null
        this.fieldErrors = {}
      },

      syncStore() {
        const strings = this.config.strings || {}
        Alpine.store('bookingInvoice', {
          wantsInvoice: this.serverWantsInvoice,
          isSaved: this.isSaved,
          blocksPayment: this.blocksPayment,
          paymentBlockMessage:
            strings.paymentBlocked ||
            'Salva i dati di fatturazione prima di procedere al pagamento.',
        })
      },

      applyInitialData() {
        const invoicedata = this.config.invoiceData || null
        this.hasInvoiceData = !!invoicedata

        if (invoicedata) {
          this.fillForm(invoicedata, this.form)
        } else if (this.isEditable) {
          const prefill = this.config.prefill || {}
          if (prefill.holder && !this.form.holder) {
            this.form.holder = prefill.holder
          }
        }

        this.updateSavedState()
        this.syncStore()
      },

      fillForm(data, target) {
        const form = target || this.form
        Object.keys(form).forEach((key) => {
          if (data[key] !== undefined && data[key] !== null) {
            form[key] = data[key]
          }
        })
        if (!form.subject_type) {
          form.subject_type = 'private'
        }
        if (!form.country) {
          form.country = 'IT'
        }
      },

      onModalSubjectTypeChange() {
        if (this.modalForm.subject_type === 'pa') {
          this.modalForm.country = 'IT'
          this.modalForm.vatnumber = ''
          this.modalForm.pec = ''
        }
        this.fieldErrors = {}
      },

      onModalToggleWantsInvoice() {
        this.fieldErrors = {}
        this.saveError = null
      },

      applySaveResult(result) {
        const invoicedata = result.invoicedata || null
        if (invoicedata) {
          this.fillForm(invoicedata, this.form)
          this.config.invoiceData = invoicedata
          this.hasInvoiceData = true
        }
        this.config.invoiceRequested = !!result.invoice_requested
        this.updateSavedState()
      },

      async postInvoicePayload(body) {
        const strings = this.config.strings || {}
        const url =
          this.config.saveEndpoint || '/api?endpoint=save-invoice-data'
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (!res.ok || data.error) {
          throw new Error(data.error || strings.saveError || 'Errore.')
        }
        return data.result || {}
      },

      validateModal() {
        const strings = this.config.strings || {}
        const errors = {}
        const f = this.modalForm

        if (!f.subject_type) {
          errors.subject_type = strings.required || 'Campo obbligatorio.'
        }

        if (!/^[A-Za-z]{2}$/.test((f.country || '').trim())) {
          errors.country = strings.invalidCountry || 'Paese non valido.'
        }

        if (f.pec && !this.isEmail(f.pec)) {
          errors.pec = strings.invalidEmail || 'Email non valida.'
        }

        if (f.sdicode && !/^[A-Za-z0-9]{6,7}$/.test(f.sdicode.trim())) {
          errors.sdicode = strings.invalidSdi || 'Codice SDI non valido.'
        }

        if (f.subject_type === 'private') {
          if (!f.firstname.trim()) {
            errors.firstname = strings.required || 'Campo obbligatorio.'
          }
          if (!f.lastname.trim()) {
            errors.lastname = strings.required || 'Campo obbligatorio.'
          }
          if (!f.taxcode.trim()) {
            errors.taxcode = strings.required || 'Campo obbligatorio.'
          }
        } else if (f.subject_type === 'pa') {
          f.country = 'IT'
          if (!f.holder.trim()) {
            errors.holder = strings.required || 'Campo obbligatorio.'
          }
          if (!f.taxcode.trim()) {
            errors.taxcode = strings.required || 'Campo obbligatorio.'
          }
          if (!f.sdicode.trim()) {
            errors.sdicode = strings.required || 'Campo obbligatorio.'
          } else if (!/^[A-Za-z0-9]{6,7}$/.test(f.sdicode.trim())) {
            errors.sdicode =
              strings.invalidPaOfficeCode ||
              strings.invalidSdi ||
              'Codice univoco ufficio non valido.'
          }
        } else {
          if (!f.holder.trim()) {
            errors.holder = strings.required || 'Campo obbligatorio.'
          }
          if (!f.vatnumber.trim() && !f.taxcode.trim()) {
            errors.vatnumber = strings.needVatOrTax || 'P.IVA o codice fiscale.'
          }
          if (!f.sdicode.trim() && !f.pec.trim()) {
            errors.sdicode = strings.needSdiOrPec || 'SDI o PEC.'
          }
        }

        this.fieldErrors = errors
        return Object.keys(errors).length === 0
      },

      isEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      },

      async submit() {
        if (this.saving || !this.isEditable) {
          return
        }
        this.saveError = null
        this.saved = false
        this.saving = true
        const strings = this.config.strings || {}

        try {
          let result
          if (!this.modalWantsInvoice) {
            result = await this.postInvoicePayload({
              booking_sid: this.config.bookingSid || '',
              invoice_requested: false,
            })
          } else {
            if (!this.validateModal()) {
              this.saving = false
              return
            }
            result = await this.postInvoicePayload({
              booking_sid: this.config.bookingSid || '',
              invoice_requested: true,
              ...this.modalForm,
            })
          }

          this.applySaveResult(result)
          this.saving = false
          this.modalOpen = false
          this.syncStore()
        } catch (err) {
          this.saveError =
            err.message || strings.saveError || 'Errore durante il salvataggio.'
          this.saving = false
        }
      },

      subjectTypeLabel(value) {
        const found = (this.config.subjectTypes || []).find(
          (st) => st.value === value
        )
        return found ? found.label : value
      },

      countryLabel(value) {
        const found = (this.config.countries || []).find(
          (c) => c.value === value
        )
        return found ? found.label : value
      },
    }))
  })
}
